import type { GridRowId, GridSlots, PropsFromSlot } from '@mui/x-data-grid-pro';
import {
  GridToolbarContainer,
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import IconButton from '@standardsdigital/ui-components/IconButton';
import { useTranslation } from 'react-i18next';
import styles from './Toolbar.module.css';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useRef, useState } from 'react';
import type { CollectionModel, StandardModel } from 'redux/services/api.generated';
import {
  LineStatus,
  useBulkReactivateSubscriptionLinesMutation,
} from 'redux/services/api.generated';
import { useToast } from 'util/hooks';
import ReactivationModal from 'components/pages/SubscriptionLines/SubscriptionLinesDataGrid/ReactivationModal/ReactivationModal';
import { downloadFile } from 'util/fileUtil';
import DiscontinueSubscriptionLineModal from '../../DiscontinueSubscriptionLineModal/DiscontinueSubscriptionLineModal';
import Button from '@standardsdigital/ui-components/Button';
import Toggle from '@standardsdigital/ui-components/Toggle';
import TextInput from '@standardsdigital/ui-components/TextInput';

interface ToolbarProps extends PropsFromSlot<GridSlots['toolbar']> {
  readonly enableSearch: boolean;
  readonly includeStandardsInCollections: boolean;
  readonly setIncludeStandardsInCollections: Dispatch<SetStateAction<boolean>>;
  readonly refetch: () => void;
  readonly datePickerMaxDate?: string;
  readonly subscriptionId: string;
}

function Toolbar({
  enableSearch,
  includeStandardsInCollections,
  setIncludeStandardsInCollections,
  refetch,
  datePickerMaxDate,
  subscriptionId,
}: ToolbarProps) {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const toast = useToast();
  const selectedRows = useGridSelector(apiRef, gridRowSelectionIdsSelector) as Map<
    GridRowId,
    StandardModel | CollectionModel
  >;
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [discontinueModalOpen, setDiscontinueModalOpen] = useState(false);
  const [reactivationModalState, setReactivationModalState] = useState({
    open: false,
    warningMessage: '',
  });
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const [reactivateSubscriptionLines] = useBulkReactivateSubscriptionLinesMutation();

  const itemsToReactivate: number[] = [];
  selectedRows.forEach((row) => {
    if (
      [
        LineStatus.Inactive,
        LineStatus.Expired,
        LineStatus.PendingInactive,
        LineStatus.Discontinued,
      ].includes(row.lineStatus)
    ) {
      itemsToReactivate.push(row.id);
    }
  });

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const debounceTimeout = 300;

    timerRef.current = setTimeout(() => {
      apiRef.current.upsertFilterItem({
        id: 'productSearch',
        field: 'name',
        operator: 'contains',
        value: event.target.value,
      });
    }, debounceTimeout);
  };

  const products: Array<{
    id: number;
    name: string;
    status: StandardModel['lineStatus'];
  }> = [];
  const collections: Array<{
    id: number;
    name: string;
    status: CollectionModel['lineStatus'];
  }> = [];
  selectedRows.forEach((row) => {
    // Use 'collectionStandardCount' to determine if it's a collection or a product.
    if ('collectionStandardCount' in row) {
      collections.push({
        id: row.id,
        name: row.name ? row.name : '',
        status: row.lineStatus,
      });
    } else {
      products.push({
        id: row.id,
        name: row.name ? row.name : '',
        status: row.lineStatus,
      });
    }
  });

  const onReactivateClick = () => {
    if (!itemsToReactivate?.length) {
      setReactivationModalState({
        open: true,
        warningMessage: t('subscriptions.noItemsToReactivate'),
      });
      return;
    }

    if (itemsToReactivate.length !== selectedRows.size) {
      setReactivationModalState({
        open: true,
        warningMessage: t('subscriptions.numberOfNumberWillBeReactivated', {
          number: itemsToReactivate.length,
          total: selectedRows.size,
        }),
      });
    } else {
      reactivateSubscriptionLines({
        bulkReactivateSubscriptionLinesCommand: {
          subscriptionLineIds: itemsToReactivate,
        },
      })
        .unwrap()
        .then(() => {
          refetch();
          toast.success(t('subscriptions.successfullyReactivated'));
        })
        .catch(() => {
          // Errors are caught in middleware.
        });
    }
  };

  const onExportClick = async () => {
    const itemsToExport: number[] = [];
    selectedRows.forEach((row) => {
      itemsToExport.push(row.id);
    });
    setIsDownloadingFile(true);
    const result = await downloadFile(
      '/api/subscription-lines/export',
      '',
      JSON.stringify({
        subscriptionId,
        subscriptionLineIds: itemsToExport,
      }),
    );
    if (!result) {
      toast.error(t('general.exportSelectedError'));
    }
    setIsDownloadingFile(false);
  };

  return (
    <GridToolbarContainer className={styles.toolbar}>
      <ReactivationModal
        warningMessage={reactivationModalState.warningMessage}
        open={reactivationModalState.open}
        setModalState={setReactivationModalState}
        itemsToReactivate={itemsToReactivate}
        refetch={refetch}
      />
      <DiscontinueSubscriptionLineModal
        isOpen={discontinueModalOpen}
        setIsOpen={setDiscontinueModalOpen}
        subscriptionLines={collections.length > 0 ? collections : products}
        refetch={refetch}
        datePickerMaxDate={datePickerMaxDate}
      />
      <div className={styles.buttonContainer}>
        <IconButton
          size="small"
          icon="CalendarOff"
          disabled={!selectedRows.size}
          onClick={() => setDiscontinueModalOpen(true)}
        >
          {t('subscriptions.deactivateSelected')}
        </IconButton>
        <IconButton
          size="small"
          icon="Recycle"
          disabled={!selectedRows.size}
          onClick={onReactivateClick}
        >
          {t('subscriptions.reactivateSelected')}
        </IconButton>
        <IconButton
          size="small"
          icon="Download"
          loading={isDownloadingFile}
          disabled={!selectedRows.size || isDownloadingFile}
          onClick={() => onExportClick()}
          className={styles.downloadButton}
        >
          {t('general.exportSelected')}
        </IconButton>
      </div>
      {Boolean(setIncludeStandardsInCollections) && (
        <Toggle
          checked={includeStandardsInCollections}
          onCheckedChange={(checked) => setIncludeStandardsInCollections(checked)}
          label={t('subscriptions.includeStandardsInCollection')}
          size="large"
        />
      )}
      {enableSearch ? (
        <TextInput
          value={inputValue}
          label={t('general.productSearch')}
          type="search"
          clearButtonProps={{ text: t('general.clearSearch') }}
          inputSize="small"
          className={styles.productSearch}
          onChange={onInputChange}
        />
      ) : null}
      <Button
        size="small"
        onClick={() => {
          setInputValue('');
          apiRef.current.setFilterModel({
            items: [{ field: 'product', operator: 'contains', value: '' }],
          });
        }}
      >
        {t('general.resetFilters')}
      </Button>
    </GridToolbarContainer>
  );
}

export default Toolbar;
